import React, { useState, useEffect } from 'react';
import { Phone, Mail, X, MapPin, ArrowRight, ChevronRight } from 'lucide-react';
import Portfolio from './Portfolio';
import { getImageUrl } from '../hooks/useImageStorage';

const HERO_IMAGES = [
  { path: 'IMG_7782.jpeg', alt: 'Modern glass railing installation' },
  { path: 'IMG_7779.jpeg', alt: 'Frameless glass railing' },
  { path: 'IMG_7783.jpeg', alt: 'Glass railing exterior' },
  { path: 'IMG_0713.jpeg', alt: 'Glass installation project' },
  { path: 'IMG_3179.jpeg', alt: 'Premium glass work' },
];

const STATS = [
  { value: '6+', label: 'Years Experience' },
  { value: '100+', label: 'Projects Completed' },
  { value: '4.9★', label: 'Customer Rating' },
  { value: '2', label: 'Regions Served' },
];

const Hero = () => {
  const [showModal, setShowModal] = useState(false);
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = (activeIndex + 1) % HERO_IMAGES.length;
      setPrevIndex(activeIndex);
      setTransitioning(true);
      setActiveIndex(next);
      setTimeout(() => {
        setPrevIndex(null);
        setTransitioning(false);
      }, 1000);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeIndex]);

  const goTo = (idx: number) => {
    if (idx === activeIndex || transitioning) return;
    setPrevIndex(activeIndex);
    setTransitioning(true);
    setActiveIndex(idx);
    setTimeout(() => {
      setPrevIndex(null);
      setTransitioning(false);
    }, 1000);
  };

  return (
    <div className="relative min-h-screen flex items-center hero-gradient overflow-hidden">
      {/* Background slideshow */}
      <div className="absolute inset-0">
        {/* Previous image fading out */}
        {prevIndex !== null && (
          <img
            key={`prev-${prevIndex}`}
            src={getImageUrl(HERO_IMAGES[prevIndex].path)}
            alt={HERO_IMAGES[prevIndex].alt}
            className="absolute inset-0 w-full h-full object-cover scale-105"
            style={{ opacity: 0, transition: 'opacity 1s ease-in-out' }}
          />
        )}
        {/* Active image fading in */}
        <img
          key={`active-${activeIndex}`}
          src={getImageUrl(HERO_IMAGES[activeIndex].path)}
          alt={HERO_IMAGES[activeIndex].alt}
          className="absolute inset-0 w-full h-full object-cover animate-slow-zoom"
          style={{ opacity: 1, transition: 'opacity 1s ease-in-out' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/80 to-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
      </div>

      {/* Decorative line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-brand-500 to-transparent opacity-60" />

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <div className="max-w-2xl">
          {/* Label */}
          <div className="flex items-center gap-2 mb-6">
            <div className="h-px w-8 bg-gold-400" />
            <span className="section-label text-gold-400">Premium Glass Solutions</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-[1.05] tracking-tight">
            Glass Railing Installation in Seattle, WA
          </h1>

          <p className="text-base sm:text-lg text-slate-300 mb-10 leading-relaxed max-w-xl">
            Seattle's trusted custom glass railing contractor — serving Seattle, WA and the Greater Vancouver, BC area. Where precision meets elegance.
          </p>

          <div className="flex flex-wrap gap-4 mb-16">
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary flex items-center gap-2 text-base px-8 py-4"
            >
              Get Free Quote
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowPortfolio(true)}
              className="btn-outline flex items-center gap-2 text-base px-8 py-4"
            >
              View Our Work
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowPickupModal(true)}
              className="btn-outline flex items-center gap-2 text-base px-8 py-4"
            >
              Pickup Order
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {STATS.map(({ value, label }) => (
              <div key={label}>
                <div className="font-display text-2xl sm:text-3xl font-bold text-white mb-1">{value}</div>
                <div className="text-xs text-slate-400 font-medium tracking-wide">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-20 right-6 sm:right-10 flex flex-col gap-2 z-10">
        {HERO_IMAGES.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`w-1.5 rounded-full transition-all duration-500 ${
              idx === activeIndex ? 'h-8 bg-gold-400' : 'h-3 bg-white/30 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* Phone bar */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-slate-950/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-wrap gap-3 justify-center sm:justify-start items-center">
          <a
            href="tel:3014335323"
            className="flex items-center gap-2 text-white hover:text-gold-400 transition-colors text-sm font-semibold"
          >
            <Phone className="w-4 h-4 text-brand-400" />
            (301) 433-5323
          </a>
          <span className="text-white/20 hidden sm:block">|</span>
          <a
            href="tel:6723995323"
            className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors text-sm font-medium"
          >
            <Phone className="w-3.5 h-3.5 text-brand-400/60" />
            (672) 399-5323
          </a>
          <span className="text-white/20 hidden sm:block">|</span>
          <span className="text-slate-400 text-sm">Serving Seattle, WA &amp; Greater Vancouver, BC</span>
        </div>
      </div>

      {/* Quote Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-slate-900">Get Your Free Quote</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-slate-500 text-sm mb-4">Choose your preferred contact method:</p>
              <a href="tel:3014335323" className="flex items-center justify-center gap-2 w-full bg-brand-600 hover:bg-brand-700 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors">
                <Phone className="w-4 h-4" /> (301) 433-5323
              </a>
              <a href="tel:6723995323" className="flex items-center justify-center gap-2 w-full border border-brand-300/40 text-white/80 hover:text-white hover:bg-white/5 px-6 py-3.5 rounded-xl font-medium transition-colors">
                <Phone className="w-4 h-4" /> (672) 399-5323
              </a>
              <a href="mailto:info@primeviewglass.com?subject=Quote%20Request" className="flex items-center justify-center gap-2 w-full border-2 border-brand-200 text-brand-600 hover:bg-brand-50 px-6 py-3.5 rounded-xl font-semibold transition-colors">
                <Mail className="w-4 h-4" /> Email Us
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Pickup Modal */}
      {showPickupModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-slate-900">Pickup Location</h2>
              <button onClick={() => setShowPickupModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-brand-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Primeview Glass</p>
                  <p className="text-slate-500 text-sm">Serving Seattle, WA & Greater Vancouver, BC</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-900 mb-2 text-sm">Hours of Operation</p>
                <ul className="text-slate-500 text-sm space-y-1">
                  <li>Monday – Friday: 8:00 AM – 6:00 PM</li>
                  <li>Saturday: 9:00 AM – 2:00 PM</li>
                  <li>Sunday: Closed</li>
                </ul>
              </div>
              <a
                href="https://maps.google.com/?q=Seattle,+WA"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-brand-600 hover:bg-brand-700 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
              >
                <MapPin className="w-4 h-4" /> Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      )}

      <Portfolio isOpen={showPortfolio} onClose={() => setShowPortfolio(false)} />
    </div>
  );
};

export default Hero;
